/**
 * Utility functions for calculating allocated hours from team members
 * and enriching budget data with team allocation information
 */

import type { ResourceBudget, RoleBudget } from '@/types/budget.types';
import type { RoleCoverage } from './mock-scheduling-data';

interface TeamMember {
  role: string;
  allocatedHours: number;
}

/**
 * Calculate allocated hours per role from team members
 */
export function calculateAllocatedHoursByRole(teamMembers: TeamMember[]): Map<string, number> {
  const allocatedByRole = new Map<string, number>();
  
  teamMembers.forEach((member) => {
    const currentAllocated = allocatedByRole.get(member.role) || 0;
    allocatedByRole.set(member.role, currentAllocated + member.allocatedHours);
  });
  
  return allocatedByRole;
}

/**
 * Enrich resource budget data with allocated hours from team members
 */
export function enrichBudgetWithAllocatedHours(
  budgetData: ResourceBudget,
  teamMembers: TeamMember[]
): ResourceBudget {
  // Calculate allocated hours by role
  const allocatedByRole = calculateAllocatedHoursByRole(teamMembers);
  
  // Enrich byRole data with allocated hours
  const enrichedByRole: RoleBudget[] = budgetData.byRole.map((role) => ({
    ...role,
    allocatedHours: allocatedByRole.get(role.role) || 0,
  }));
  
  return {
    ...budgetData,
    byRole: enrichedByRole,
  };
}

/**
 * Use live scheduler coverage values for a role when the scheduler is connected.
 */
export function applySchedulerRoleCoverageOverride(
  role: RoleBudget,
  schedulerConnected: boolean,
  roleCoverageData?: RoleCoverage[],
): RoleBudget {
  if (!schedulerConnected) return role;

  const coverage = (roleCoverageData ?? []).find(
    (entry) => entry.roleName.trim().toLowerCase() === role.role.trim().toLowerCase(),
  );
  if (!coverage) return role;

  const actualHours = coverage.actualHours;
  const remainingHours = role.budgetHours - actualHours;
  const percentUsed = role.budgetHours > 0
    ? (actualHours / role.budgetHours) * 100
    : 0;

  return {
    ...role,
    allocatedHours: coverage.scheduledHours,
    actualHours,
    remainingHours,
    percentUsed,
  };
}
